import type { AuthState, TokenPrincipal } from './types.js';

/** A tiny observable state holder shared by the real controller and the test double. */
export class AuthStore {
  #listeners = new Set<() => void>();
  #signOutListeners = new Set<() => void>();
  #state: AuthState = { principal: null, status: 'initializing' };

  getState(): AuthState {
    return this.#state;
  }

  set(state: AuthState): void {
    this.#state = state;
    for (const listener of this.#listeners) {
      listener();
    }
  }

  subscribe(listener: () => void): () => void {
    this.#listeners.add(listener);
    return () => {
      this.#listeners.delete(listener);
    };
  }

  onSignOut(listener: () => void): () => void {
    this.#signOutListeners.add(listener);
    return () => {
      this.#signOutListeners.delete(listener);
    };
  }

  /** Runs the sign-out listeners (query-cache clears) exactly once per sign-out. */
  signedOut(status: 'unauthenticated' | 'session-expired'): void {
    this.set({ principal: null, status });
    for (const listener of this.#signOutListeners) {
      listener();
    }
  }
}

function decodeSegment(segment: string): Record<string, unknown> {
  const base64 = segment.replace(/-/g, '+').replace(/_/g, '/');
  const padded = base64.padEnd(base64.length + ((4 - (base64.length % 4)) % 4), '=');
  const binary = atob(padded);
  const bytes = Uint8Array.from(binary, (character) => character.charCodeAt(0));
  const parsed: unknown = JSON.parse(new TextDecoder().decode(bytes));
  return parsed !== null && typeof parsed === 'object' ? (parsed as Record<string, unknown>) : {};
}

/**
 * Reads display claims from a JWT access token without verifying it. The claims are hints for the
 * UI only; the API verifies the token and resolves roles from SAAY assignments (AC §6.2).
 */
export function principalFromAccessToken(
  accessToken: string,
  profile: Record<string, unknown> = {},
): TokenPrincipal {
  let claims: Record<string, unknown> = {};
  try {
    claims = decodeSegment(accessToken.split('.')[1] ?? '');
  } catch {
    claims = {};
  }
  const merged = { ...profile, ...claims };
  const realmAccess = merged.realm_access as { roles?: unknown } | undefined;
  const roles = Array.isArray(realmAccess?.roles)
    ? realmAccess.roles.filter((role): role is string => typeof role === 'string')
    : [];
  const amr = Array.isArray(merged.amr)
    ? merged.amr.filter((method): method is string => typeof method === 'string')
    : [];
  const name =
    typeof merged.name === 'string'
      ? merged.name
      : typeof merged.preferred_username === 'string'
        ? merged.preferred_username
        : null;

  return {
    acr: typeof merged.acr === 'string' ? merged.acr : null,
    amr,
    authorizedParty: typeof merged.azp === 'string' ? merged.azp : null,
    displayName: name,
    expiresAt: typeof merged.exp === 'number' ? merged.exp : null,
    roles,
    subject: typeof merged.sub === 'string' ? merged.sub : '',
  };
}

/** Keeps only same-app relative routes, so a crafted `returnTo` can never leave the app. */
export function safeReturnTo(value: unknown, fallback = '/'): string {
  // `//host` and `/\host` are protocol-relative URLs in browsers.
  if (typeof value !== 'string' || !/^\/(?![/\\])/.test(value)) {
    return fallback;
  }
  if (value.startsWith('/auth/')) {
    return fallback;
  }
  return value;
}
